/**
 * Symbol-column chrome for Wiseguy: earnings E + 52W from QF meta only.
 * Never recompute earnings windows client-side.
 */

import type {
  SteamroomEarningsPhase,
  SteamroomEarningsTime,
  SteamroomTickerMeta,
} from '../../services/fznService';

export type EarningsPhase = SteamroomEarningsPhase;

const PHASE_TITLE: Record<EarningsPhase, string> = {
  pre: 'Pre-earnings',
  day_of: 'Day-of earnings',
  post: 'Post-earnings',
};

const TIME_LABEL: Record<SteamroomEarningsTime, string> = {
  bmo: 'BMO',
  amc: 'AMC',
  dmh: 'DMH',
};

/** Tailwind classes: pre red, day_of yellow, post green. */
export const EARNINGS_PHASE_CLASS: Record<EarningsPhase, string> = {
  pre: 'text-bear-red',
  day_of: 'text-accent-yellow',
  post: 'text-bull-blue',
};

export const WEEK52_CLASS: Record<'high' | 'low', string> = {
  high: 'text-bull-blue',
  low: 'text-bear-red',
};

/**
 * Build earnings badge from server phase only.
 * Date for tooltip: post prefers last_earnings; else next_earnings.
 */
export function earningsBadge(
  meta: SteamroomTickerMeta | null | undefined
): { phase: EarningsPhase; title: string } | null {
  if (!meta) return null;
  const phase = meta.earnings_phase;
  if (phase !== 'pre' && phase !== 'day_of' && phase !== 'post') return null;

  const date =
    phase === 'post'
      ? (meta.last_earnings ?? meta.next_earnings)
      : (meta.next_earnings ?? meta.last_earnings);

  const bits = [PHASE_TITLE[phase]];
  if (date) bits.push(date);
  if (meta.earnings_time) bits.push(TIME_LABEL[meta.earnings_time]);

  return { phase, title: bits.join(' · ') };
}

export function week52Badge(
  meta: SteamroomTickerMeta | null | undefined
): { kind: 'high' | 'low'; title: string } | null {
  if (!meta) return null;
  if (meta.week52 === 'high') {
    return { kind: 'high', title: 'At or above 52-week high' };
  }
  if (meta.week52 === 'low') {
    return { kind: 'low', title: 'At or below 52-week low' };
  }
  return null;
}

/** Curator glyphs — match QF house chrome (trophy / broom). */
export const TOP_BET_GLYPH = '🏆';
export const TOP_SWEEP_GLYPH = '🧹';
